import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'reactstrap';
import { v4 as uuid } from 'uuid';

const EVENT_TYPE_KEYDOWN = 'keydown';
const EVENT_KEYCODE_SPACEBAR = ' ';

const propTypes = {

	/**
	 * The CSS classes (if any) to apply to the dropdown.
	 */
	className: PropTypes.string,

	/**
	 * The dropdown content (DropdownToggle, DropdownMenu, DropdownItems, etc).
	 */
	children: PropTypes.node.isRequired,

};

/**
 * Shared component to render a TechChange-style dropdown.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const TCDropdown = (props) => {

	const { children, className, ...propsToPass } = props;

	const [isOpen, setIsOpen] = useState(false);
	const [focusOnSpacebarKeypress, setFocusOnSpacebarKeypress] = useState(false);

	// Create a unique ID for the dropdown
	const dropdownID = useMemo(() => `tc-dropdown-${uuid()}`, []);

	// Override toggle behavior to support opening the dropdown menu via spacebar on Firefox
	// https://github.com/reactstrap/reactstrap/issues/1945
	useEffect(() => {

		// If the dropdown menu is opened via a keypress of the spacebar...
		if (isOpen && focusOnSpacebarKeypress) {

			// Try to auto-focus the first active menu item

			const firstActiveMenuItem = document.querySelector(
				`#${dropdownID} .dropdown-item:not(.disabled)`
			);

			if (firstActiveMenuItem) {
				firstActiveMenuItem.focus();
			}

		}

	}, [isOpen, focusOnSpacebarKeypress, dropdownID]);

	return (
		<Dropdown
			{...propsToPass}
			id={dropdownID}
			className={`tc-dropdown ${className || ''}`}
			isOpen={isOpen}
			toggle={(event) => {

				// Check if we're opening the menu via a keypress of the spacebar...
				if (event.type === EVENT_TYPE_KEYDOWN && event.key === EVENT_KEYCODE_SPACEBAR && !isOpen) {
					setFocusOnSpacebarKeypress(true);
					return;
				}

				// Auto-reset the spacebar keypress state if we're closing the menu...
				if (isOpen) {
					setFocusOnSpacebarKeypress(false);
				}

				setIsOpen((prev) => !prev);

			}}>
			{children}
		</Dropdown>
	);

};

TCDropdown.propTypes = propTypes;

export default TCDropdown;
